import { useFetch, useNotificationContext } from "@4uhub/lib4uhub";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { memo, useCallback, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { z } from "zod";
import { IEvaluationFormQuestion } from "../../../../../models/form-satisfaction";
import { useEvaluationFormContext } from "../../../../../store/contexts/EvaluationFormContext";
import { useFormStepperContext } from "../../../../../store/contexts/FormStepperContext";
import {
  answerEvaluationFormQuestion,
  updateAnswerEvaluationFormQuestion,
} from "../../../../../services/evaluationForm.service";
import RadioGroupInput from "../../../Inputs/RadioGroupInput";
import FormInput from "../../../Inputs/FormInput";
import { JustificationField } from "../../../../EvaluationForm/Step/JustificationField/JustificationField";
import { alternativeRequiresJustification } from "../../../../EvaluationForm/Step/justificationRules";
import { QuestionGuidanceText } from "../../../../EvaluationForm/QuestionGuidanceText";

const questionSchema = z.object({
  questionAlternative: z.string().optional(),
  justification: z.string().optional(),
  answer: z.string().optional(),
});

const buildSchema = (questions: IEvaluationFormQuestion[]) => {
  const shape: Record<string, typeof questionSchema> = {};
  questions.forEach((q) => {
    shape[q.question.id] = questionSchema;
  });
  return z.object(shape);
};

type TAllStepsForm = Record<
  string,
  { questionAlternative?: string; justification?: string; answer?: string }
>;

interface IAllStepsFormProps {
  questions: IEvaluationFormQuestion[];
  hasAnswerDate: boolean;
  onFinish: () => void;
  stepsLength: number;
}

const translationPath = "components.step.";
const LIKERT_CODES = ["7", "8", "9", "10"];
const GUIDANCE_TEXT_POSITION_BEFORE = "1";
const GUIDANCE_TEXT_POSITION_AFTER = "2";

const getAlternativeLabel = (alternative: {
  label: string | null;
  nrOption: number;
}): string =>
  alternative.label !== null && alternative.label !== undefined
    ? alternative.label
    : alternative.nrOption.toString();

export const AllStepsForm = memo(
  ({ questions, hasAnswerDate, stepsLength, onFinish }: IAllStepsFormProps) => {
    const { t } = useTranslation();
    const { id, type } = useParams();
    const { setMessage } = useNotificationContext();
    const { answers, addAnswer, updateAnswer } = useEvaluationFormContext();
    const { handleNext, activeStep } = useFormStepperContext();

    const defaultValues = useMemo<TAllStepsForm>(() => {
      const values: TAllStepsForm = {};
      questions.forEach(({ question }) => {
        const existing = answers.find((a) => a.questionId === question.id);
        values[question.id] = {
          questionAlternative: existing?.questionAlternativeId ?? "",
          justification: existing?.justification ?? "",
          answer: existing?.answer ?? "",
        };
      });
      return values;
    }, [questions, answers]);

    const schema = useMemo(() => buildSchema(questions), [questions]);

    const methods = useForm<TAllStepsForm>({
      resolver: zodResolver(schema),
      defaultValues,
    });

    const { handleSubmit, setError } = methods;

    const { sendRequest, loading: createLoading } = useFetch(
      answerEvaluationFormQuestion,
    );
    const { sendRequest: update, loading: updateLoading } = useFetch(
      updateAnswerEvaluationFormQuestion,
    );

    const loading = createLoading || updateLoading;

    const onSubmitHandler = useCallback(
      async (form: TAllStepsForm) => {
        if (!id) return;

        for (let i = 0; i < questions.length; i++) {
          const { question } = questions[i];
          const fieldValues = form[question.id] ?? {};
          const code = String(question.questionEvaluationType?.code ?? "");

          const currentAlternative = question.questionAlternatives?.find(
            (alt) => alt.id === fieldValues.questionAlternative,
          );

          const selectedOptionRequiresJustification =
            !!currentAlternative &&
            question.questionOptionType.isRequiredJustification &&
            alternativeRequiresJustification(
              currentAlternative.integrationId,
              currentAlternative.nrOption,
              question.questionOptionType.requiredJustificationOptions,
              question.questionOptionType.requiredJustificationLessThan,
            );

          if (
            (selectedOptionRequiresJustification &&
              !fieldValues.justification?.trim()) ||
            (fieldValues.justification &&
              fieldValues.justification.length <
                question.questionOptionType.requiredJustificationMinChars)
          ) {
            setError(`${question.id}.justification`, {
              type: "min",
              message: question.questionOptionType.requiredJustificationMessage,
            });
            document
              .getElementById(`question-${question.id}`)
              ?.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
          }

          if (
            question?.questionEvaluationType?.code &&
            ["1", "2", ...LIKERT_CODES].includes(code)
          ) {
            if (!fieldValues.questionAlternative) {
              setError(`${question.id}.questionAlternative`, {
                type: "min",
                message: `${t(translationPath + "errors.alternative")}`,
              });
              document
                .getElementById(`question-${question.id}`)
                ?.scrollIntoView({ behavior: "smooth", block: "center" });
              return;
            }
          }

          if (
            question?.questionEvaluationType?.code &&
            ["3", "4", "5"].includes(
              String(question.questionEvaluationType.code),
            ) &&
            question.questionOptionType?.isRequired
          ) {
            if (
              !fieldValues.answer ||
              fieldValues.answer.length < question.questionOptionType.minChars
            ) {
              setError(`${question.id}.answer`, {
                type: "min",
                message: `${t(translationPath + "errors.answer", {
                  min: question.questionOptionType.minChars,
                })}`,
              });
              document
                .getElementById(`question-${question.id}`)
                ?.scrollIntoView({ behavior: "smooth", block: "center" });
              return;
            }
          }

          let derivedAnswer = fieldValues.answer ?? undefined;
          const derivedQuestionAlternativeId = fieldValues.questionAlternative
            ? fieldValues.questionAlternative
            : undefined;

          if (currentAlternative) {
            derivedAnswer =
              currentAlternative.integrationId ??
              String(currentAlternative.nrOption);
          }

          const currentAnswer = answers.find(
            (a) => a.questionId === question.id,
          );

          if (!currentAnswer) {
            const { data, success } = await sendRequest({
              evalMovEncSatisfactionId: type ? undefined : id,
              evalMovTicketId: type ? id : undefined,
              questionAlternativeId: derivedQuestionAlternativeId,
              justification: fieldValues.justification ?? undefined,
              answer: derivedAnswer,
              questionId: question.id,
            });

            if (!success || !data) {
              return;
            }

            addAnswer({
              id: data.id,
              questionId: question.id,
              justification: fieldValues.justification ?? "",
              questionAlternativeId: derivedQuestionAlternativeId,
              answer: derivedAnswer,
            });
          } else {
            const hasChanges =
              currentAnswer.questionAlternativeId !==
                derivedQuestionAlternativeId ||
              currentAnswer.justification !== fieldValues.justification ||
              currentAnswer.answer !== derivedAnswer;

            if (hasChanges) {
              const { success } = await update({
                id: currentAnswer.id,
                questionAlternativeId: derivedQuestionAlternativeId,
                justification: fieldValues.justification ?? "",
                evalMovTicketId: type ? id : undefined,
                answer: derivedAnswer,
                questionId: question.id,
              });

              if (!success) return;

              updateAnswer({
                id: question.id,
                questionId: question.id,
                justification: fieldValues.justification ?? "",
                questionAlternativeId: derivedQuestionAlternativeId,
                answer: derivedAnswer,
              });
            }
          }
        }

        setMessage({
          message: `${t(translationPath + "answer_success")}`,
          type: "success",
        });

        for (let i = activeStep; i < stepsLength; i++) {
          handleNext();
        }

        onFinish();
      },
      [
        questions,
        id,
        type,
        answers,
        setError,
        sendRequest,
        update,
        addAnswer,
        updateAnswer,
        setMessage,
        handleNext,
        onFinish,
        t,
        activeStep,
        stepsLength,
      ],
    );

    return (
      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmitHandler)}
          sx={{ width: "100%", pb: 6 }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            {questions.map(({ question }, index) => {
              const isTextQuestion =
                question?.questionEvaluationType?.code &&
                ["3", "4", "5"].includes(question.questionEvaluationType.code);

              return (
                <Box
                  id={`question-${question.id}`}
                  key={question.id}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                    scrollMarginTop: "2rem",
                  }}
                >
                  {question.guidanceText &&
                    question.guidanceTextPosition?.code ===
                      GUIDANCE_TEXT_POSITION_BEFORE && (
                      <QuestionGuidanceText
                        guidanceText={question.guidanceText}
                      />
                    )}
                  <Typography
                    fontSize={16}
                    fontWeight="bold"
                    textAlign="center"
                  >
                    {index + 1}. {question.title}
                  </Typography>

                  {question.description && question.description.length > 0 && (
                    <Typography
                      variant="subtitle2"
                      textAlign="center"
                      sx={(theme) => ({
                        color:
                          theme.palette.grey[
                            theme.palette.mode === "light" ? 600 : 300
                          ],
                      })}
                    >
                      {question.description}
                    </Typography>
                  )}

                  <Box
                    sx={{
                      mt: 1,
                      width: "100%",
                      maxWidth: 600,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    {isTextQuestion ? (
                      <FormInput
                        name={`${question.id}.answer`}
                        size="small"
                        fullWidth
                        multiline
                        minRows={4}
                        disabled={hasAnswerDate}
                      />
                    ) : (
                      <>
                        <RadioGroupInput
                          name={`${question.id}.questionAlternative`}
                          groupLabel=""
                          disableBorder
                          inline
                          fullwidth
                          disabled={hasAnswerDate}
                          radios={[...question.questionAlternatives]
                            .sort((a, b) => a.nrOption - b.nrOption)
                            .map((alternative) => ({
                              label: getAlternativeLabel(alternative),
                              value: alternative.id,
                            }))}
                        />
                        <JustificationField
                          requiredJustificationLessThan={
                            question.questionOptionType
                              .requiredJustificationLessThan
                          }
                          requiredJustificationOptions={
                            question.questionOptionType.requiredJustificationOptions
                          }
                          questionAlternatives={question.questionAlternatives}
                          hasAnswerDate={hasAnswerDate}
                          alwaysShowJustification={
                            question.questionOptionType.alwaysShowJustification
                          }
                          isRequiredJustification={
                            question.questionOptionType.isRequiredJustification
                          }
                          requiredJustificationMessage={
                            question.questionOptionType.requiredJustificationMessage
                          }
                          fieldNamePrefix={question.id}
                        />
                      </>
                    )}
                  </Box>
                  {question.guidanceText &&
                    question.guidanceTextPosition?.code ===
                      GUIDANCE_TEXT_POSITION_AFTER && (
                      <QuestionGuidanceText
                        guidanceText={question.guidanceText}
                      />
                    )}
                </Box>
              );
            })}
          </Box>

          {!hasAnswerDate && (
            <Box
              sx={{
                mt: 6,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : undefined
                }
                sx={{ minWidth: 160 }}
              >
                {loading
                  ? t(translationPath + "saving")
                  : t(translationPath + "save")}
              </Button>
            </Box>
          )}
        </Box>
      </FormProvider>
    );
  },
);
